<script setup>
import { watchDebounced } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import moment from "moment";
import axios from '@/helpers/axios'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'

definePage({
  meta: {
    layout: 'default',
    role: 'user',
    layoutWrapperClasses: 'layout-content-height-fixed',
  },
})

const { t } = useI18n()
const router = useRouter()
const user = ref(useCookie('userData').value)


const { isLeftSidebarOpen } = useResponsiveLeftSidebar()

const searchQuery = ref('')
const rowPerPage = ref(10)
const currentPage = ref(1)
const totalPage = ref(1)
const totalItems = ref(0)
const items = ref([])

const filterFavorite = ref(false)
const filterOnline = ref(false)
const filterDelayed = ref(false)

const filterFromGroups = ref(true)
const filterPublic = ref(false)

const isLoading = ref(false)
const isLoadingMore = ref(false)
const activeRequestId = ref(0)

const resetPagination = () => {
  currentPage.value = 1
  totalPage.value = 1
  totalItems.value = 0
}

const getData = async (isLoadMore = false) => {
  const requestId = activeRequestId.value + 1
  activeRequestId.value = requestId

  if (isLoadMore) {
    isLoadingMore.value = true
  } else {
    isLoading.value = true
  }

  try {
    const { data } = await axios.get('/devices', {
      params: {
        q: searchQuery.value,
        perPage: rowPerPage.value,
        currentPage: currentPage.value,
        online: filterOnline.value === true ? 1 : 0,
        delayed: filterDelayed.value === true ? 1 : 0,
        favorite: filterFavorite.value === true ? 1 : 0,
        groups: filterFromGroups.value === true ? 1 : 0,
        public: filterPublic.value === true ? 1 : 0,
      },
    })

    if (requestId !== activeRequestId.value)
      return

    const devices = data?.data ?? []

    items.value = isLoadMore ? [...items.value, ...devices] : devices
    totalPage.value = data?.meta?.last_page ?? 1
    totalItems.value = data?.meta?.total ?? devices.length
  } catch (error) {
    console.error('Failed to fetch devices', error)
  } finally {
    if (requestId === activeRequestId.value) {
      if (isLoadMore) {
        isLoadingMore.value = false
      } else {
        isLoading.value = false
      }
    }
  }
}

const handleRefetch = () => {
  if (isLoadingMore.value)
    isLoadingMore.value = false

  resetPagination()
  getData()
}

watch(
  [
    filterOnline,
    filterDelayed,
    filterFavorite,
    filterFromGroups,
    filterPublic,
    rowPerPage,
  ],
  handleRefetch,
)

watchDebounced(searchQuery, handleRefetch, { debounce: 400, maxWait: 1000 })

const hasDevices = computed(() => items.value.length > 0)
const hasMoreDevices = computed(() => currentPage.value < totalPage.value)
const isEmptyStateVisible = computed(() => !isLoading.value && !hasDevices.value)

const loadMore = () => {
  if (isLoadingMore.value || !hasMoreDevices.value)
    return

  currentPage.value += 1
  getData(true)
}

const toggleFavorite = uuid => {
  axios.post('/devices/' + uuid + '/favorite').then(response => {
    items.value = items.value.map(item => {
      if (item.uuid === uuid) {
        item.favorite = !item.favorite
      }
      return item
    })
  }).catch(e => {
    console.error(e)
  })
}

onMounted(() => {
  getData()
})


const resolveStatusColor = stat => {
  switch (stat) {
  case 'online':
    return 'primary'
  case 'delayed':
    return 'warning'
  case 'hibernated':
    return 'error'
  default:
    return 'secondary'
  }
}

const resolveStatusIcon = stat => {
  switch (stat) {
    case 'online':
      return 'tabler-wifi'
    case 'delayed':
      return 'tabler-clock-hour-4'
    case 'hibernated':
      return 'tabler-player-pause-filled'
    default:
      return 'tabler-settings'
  }
}

const resolveStatusText = stat => {
  switch (stat) {
    case 'online':
      return t('devices.online')
    case 'delayed':
      return t('devices.delayed')
    case 'hibernated':
      return t('devices.hibernated')
    default:
      return t('devices.unknown')
  }
}

function goToDevice(uuid) {
  router.push({ name: 'devices-uuid', params: { uuid } })
}
</script>

<template>
  <VLayout
    style="min-block-size: 100%;"
    class="email-app-layout"
  >
    <VNavigationDrawer
      v-model="isLeftSidebarOpen"
      absolute
      touchless
      location="start"
      :temporary="$vuetify.display.mdAndDown"
    >
      <div class="d-flex flex-column h-100">
        <!-- 👉 Folders -->
        <PerfectScrollbar
          :options="{ wheelPropagation: false }"
          class="h-100"
        >
          <!-- Filters -->
          <ul class="email-labels">
            <li class="text-xs d-block text-uppercase text-disabled mt-6 mb-2">
              {{ $t('devices.filters') }}
            </li>
            <li
              v-bind="$attrs"
              :class="filterOnline && 'email-label-active text-primary'"
              class="cursor-pointer"
              @click="filterOnline = !filterOnline"
            >
              <VAvatar
                color="success"
                icon="tabler-wifi"
                variant="plain"
                size="x-small"
                class="me-4"

              />
              <span class="font-weight-medium">{{ $t('devices.online') }}</span>
            </li>
            <li
              v-bind="$attrs"
              :class="filterDelayed && 'email-label-active text-primary'"
              class="cursor-pointer"
              @click="filterDelayed = !filterDelayed"
            >
              <VAvatar
                color="error"
                icon="tabler-clock-hour-4"
                variant="plain"
                size="x-small"
                class="me-4"

              />
              <span class="font-weight-medium">{{ $t('devices.delayed') }}</span>
            </li>
            <li
              v-bind="$attrs"
              :class="filterFavorite && 'email-label-active text-primary'"
              class="cursor-pointer"
              @click="filterFavorite = !filterFavorite"
            >
              <VAvatar
                color="warning"
                icon="tabler-star"
                variant="plain"
                size="x-small"
                class="me-4"

              />
              <span class="font-weight-medium">{{ $t('devices.favorite') }}</span>
            </li>
            <VDivider class="my-2" />
            <li>
              <VCheckbox
                v-model="filterFromGroups"
                class="text-primary font-weight-medium"
                :label="$t('devices.shared_filter')"
              />
            </li>
            <li>
              <VCheckbox
                v-model="filterPublic"
                class="text-primary font-weight-medium"
                :label="$t('devices.public_filter')"
              />
            </li>
          </ul>
        </PerfectScrollbar>
      </div>
    </VNavigationDrawer>
    <VMain>
      <VCard
        flat
        class="email-content-list h-100 d-flex flex-column"
      >
        <div class="d-flex align-center">
          <IconBtn
            class="d-lg-none ms-3"
            @click="isLeftSidebarOpen = true"
          >
            <VIcon icon="tabler-menu-2" />
          </IconBtn>

          <!-- 👉 Search -->
          <VTextField
            v-model="searchQuery"
            density="default"
            class="email-search px-1 flex-grow-1"
            prepend-inner-icon="tabler-search"
            :placeholder="$t('devices.search_device')"
          />
        </div>
        <VDivider />
        <VProgressLinear
          v-if="isLoading"
          indeterminate
          color="primary"
          height="2"
          class="flex-shrink-0"
        />
        <!-- 👉 Devices list -->
        <PerfectScrollbar
          tag="ul"
          :options="{ wheelPropagation: false }"
          class="email-list"
        >
        
          <li
            v-for="item in items"
            :key="item.uuid"
            class="email-item d-flex align-center py-2 px-5 cursor-pointer"
            @click="goToDevice(item.uuid)"
          >
            <VTooltip location="top">
              <template v-slot:activator="{ props }">
                <span v-bind="props">
                  <VAvatar 
                    :color="resolveStatusColor(item.status)"
                    :icon="resolveStatusIcon(item.status)"
                    class="mx-2"
                    size="32"
                  />
                </span>
              </template>
              <span>
                {{ resolveStatusText(item.status) }}
              </span>
            </VTooltip>

            <h6 class="mx-3 text-body-1 font-weight-medium text-high-emphasis">
              {{ item.name }}
            </h6>
            <span class="truncate">
              {{ item.model }}
            </span>
            <VSpacer />

            <div
              class="email-meta"
              :class="$vuetify.display.xs ? 'd-none' : ''"
            >
              <VTooltip v-if="item.in_groups" location="top">
                <template v-slot:activator="{ props }">
                  <span v-bind="props">
                    <VAvatar
                      color="primary"
                      icon="tabler-users"
                      variant="plain"
                      size="x-small"
                    />
                  </span>
                </template>
                <span>
                  {{ t('devices.from_group') }}
                </span>
              </VTooltip>

              <VTooltip v-if="item.public" location="top">
                <template v-slot:activator="{ props }">
                  <span v-bind="props">
                    <VAvatar
                      color="info"
                      icon="tabler-lock-open-2"
                      variant="plain"
                      size="x-small"
                    />
                  </span>
                </template>
                <span>
                  {{ t('devices.public') }}
                </span>
              </VTooltip>

              <small class="text-disabled text-sm ms-2">
                {{ item.last_data ? moment.utc(item.last_data).local().format("Y-MM-DD HH:mm") : $t('devices.no_data') }}
              </small>

              <span @click.stop.prevent>
                <IconBtn
                  :color="item.favorite ? 'warning' : 'default'"
                  @click.stop="toggleFavorite(item.uuid)"
                  class="ml-2"
                >
                  <VIcon
                    :icon="item.favorite ? 'tabler-star-filled' : 'tabler-star'"
                    :class="item.favorite ? '' : 'text-disabled'"
                  />
                </IconBtn>
              </span>

              
            </div>
          </li>
          <li v-if="hasMoreDevices" class="text-center">
            <!-- Load More Button -->
            <VBtn
              variant="text"
              color="primary"
              class="mx-auto my-4"
              @click="loadMore"
              :loading="isLoadingMore"
              :disabled="isLoadingMore"
            >
              {{ $t('devices.load_more_devices') }}
            </VBtn>
          </li>
          <li
            v-if="isEmptyStateVisible"
          >
            <!-- No data -->
            <div
              class="d-flex flex-column align-center justify-center text-center flex-grow-1 py-10"
            >
              <VIcon icon="tabler-box-off" size="48" color="grey" class="mb-3" />
              <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
                {{ $t('devices.no_devices') }}
              </h4>
            </div>
          </li>
        </PerfectScrollbar>
      </VCard>
    </VMain>
  </VLayout>
</template>

<style lang="scss">
@use "@styles/variables/_vuetify.scss";
@use "@core/scss/base/_mixins.scss";

// ℹ️ Remove border. Using variant plain cause UI issue, caret isn't align in center
.email-search {
  .v-field__outline {
    display: none;
  }
}

.email-app-layout {
  border-radius: 16px;

  @include mixins.elevation(vuetify.$card-elevation);

  $sel-email-app-layout: &;

  @at-root {
    .skin--bordered {
      @include mixins.bordered-skin($sel-email-app-layout);
    }
  }
}

.email-content-list {
  border-end-start-radius: 0;
  border-start-start-radius: 0;
}

.email-list {
  white-space: nowrap;

  .email-item {
    block-size: 3.75rem;
    transition: all 0.2s ease-in-out;
    will-change: transform, box-shadow;

    &.email-read {
      background-color: rgba(var(--v-theme-on-surface), var(--v-hover-opacity));
    }

    & + .email-item {
      border-block-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
  }

  .email-item:hover {
    transform: translateY(-2px);

    @include mixins.elevation(3);

    .email-actions {
      display: block !important;
    }

/*
    .email-meta {
      display: none;
    }
*/
    + .email-item {
      border-color: transparent;
    }

    @media screen and (max-width: 600px) {
      .email-actions {
        display: none !important;
      }
    }
  }
}

.email-compose-dialog {
  position: absolute;
  inset-block-end: 0;
  inset-inline-end: 0;
  min-inline-size: 100%;

  @media only screen and (min-width: 800px) {
    min-inline-size: 712px;
  }
}

.email-filters,
.email-labels {
  > li {
    position: relative;
    margin-block-end: 4px;
    padding-block: 8px;
    padding-inline: 24px;
  }

  .email-filter-active,
  .email-label-active {
    &::after {
      position: absolute;
      background: currentcolor;
      block-size: 100%;
      content: "";
      inline-size: 3px;
      inset-block-start: 0;
      inset-inline-start: 0;
    }
  }
}

.email-labels {
  > li {
    position: relative;
    margin-block-end: 4px;
    padding-block: 4px;
    padding-inline: 24px;
  }
}
</style>
